/**
 * Section facts (SS-1c) — what a section IS, read from its own content.
 *
 * Pure and import-free, like shared/section-categories.js. Two consumers by
 * design (the row's fact line and the variant picker's cards/facet chips), so
 * neither can drift into its own copy of the heading rule or the feature
 * registry.
 *
 * The DOM boundary: `summarizeSectionDom` / `deriveSectionFeatures` take an
 * ALREADY-PARSED node, never a string — the repo forbids detecting elements
 * with string matching. The memoizing entry point `sectionFactsFor` takes the
 * html string plus an INJECTED `parse` function (the consumer owns DOMParser),
 * so this module stays environment-free and node-testable.
 *
 * Cost is a property of the code, not a claim: facts are memoized by the html
 * string itself — NEVER invalidated on `state-changed`, which fires inside
 * transactions (the 16C2 lesson) — and `_factsParseCount()` exposes the parse
 * counter so a test can assert N renders of an unchanged page perform exactly
 * one parse per distinct section body.
 *
 * Feature derivation is a DECLARED registry over two complementary signals,
 * measured before it was designed (docs/section-surfaces-contracts.md §3):
 *   - class tokens — strong for template-origin variants (`cs-top-bar` 17/31,
 *     `cs-dropdown` 18/31 across the catalog navs), useless for descriptions;
 *   - description keywords — CodeStitch descriptions ARE the feature list
 *     ("Top Bar Dropdown", "Mega Menu"), while 315 of the 421 template-origin
 *     navs are literally "Cs Navigation".
 * Unmatched → NO feature, never a guessed one. A facet must be explicable by
 * eye against the card's own tags.
 */

/** Longest heading we will surface as a derived name. */
const HEADING_MAX = 80;

/**
 * The declared feature registry. `classes` match exact class tokens,
 * `classPrefix` matches any token starting with it, `keywords` match the
 * lowercased description. Registry order is display order.
 */
export const SECTION_FEATURES = Object.freeze([
  Object.freeze({
    key: 'topBar',
    label: 'Top bar',
    classes: Object.freeze(['cs-top-bar', 'cs-top-container']),
    keywords: Object.freeze(['top bar'])
  }),
  Object.freeze({
    key: 'dropdown',
    label: 'Dropdown',
    classes: Object.freeze(['cs-dropdown', 'cs-drop-ul']),
    keywords: Object.freeze(['dropdown'])
  }),
  Object.freeze({
    key: 'mega',
    label: 'Mega menu',
    classes: Object.freeze([]),
    classPrefix: 'cs-mega',
    keywords: Object.freeze(['mega'])
  }),
  Object.freeze({
    key: 'centered',
    label: 'Centered',
    classes: Object.freeze([]),
    keywords: Object.freeze(['centered'])
  }),
  Object.freeze({
    key: 'transparent',
    label: 'Transparent',
    classes: Object.freeze(['cs-onscroll']),
    keywords: Object.freeze(['transparent'])
  }),
  Object.freeze({
    key: 'contained',
    label: 'Contained',
    classes: Object.freeze([]),
    keywords: Object.freeze(['contained', 'floating'])
  }),
  Object.freeze({
    key: 'dark',
    label: 'Dark',
    classes: Object.freeze(['cs-dark']),
    keywords: Object.freeze(['dark'])
  }),
  Object.freeze({
    key: 'cta',
    label: 'CTA button',
    classes: Object.freeze(['cs-button-solid']),
    keywords: Object.freeze([])
  })
]);

export function featureLabel(key) {
  return SECTION_FEATURES.find((f) => f.key === key)?.label || key;
}

/**
 * Read the discriminating facts out of a parsed section body.
 *
 * @param {ParentNode} root — a parsed Document, DocumentFragment or Element
 * @returns {{heading: string, imageCount: number, linkCount: number}}
 */
export function summarizeSectionDom(root) {
  if (!root || typeof root.querySelectorAll !== 'function') {
    return { heading: '', imageCount: 0, linkCount: 0 };
  }
  let heading = '';
  for (const h of root.querySelectorAll('h1,h2,h3,h4,h5,h6')) {
    const text = (h.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) {
      heading = text.length > HEADING_MAX ? `${text.slice(0, HEADING_MAX - 1)}…` : text;
      break;
    }
  }
  return {
    heading,
    imageCount: root.querySelectorAll('img').length,
    linkCount: root.querySelectorAll('a[href]').length
  };
}

/** Collect the set of class tokens present anywhere in a parsed body. */
function classTokenSet(root) {
  const tokens = new Set();
  if (!root || typeof root.querySelectorAll !== 'function') return tokens;
  for (const el of root.querySelectorAll('[class]')) {
    for (const token of el.classList) tokens.add(token);
  }
  return tokens;
}

/**
 * Which registry features does this section carry?
 *
 * @param {ParentNode|null} root — parsed body (null = keywords only)
 * @param {{description?: string}} [meta]
 * @returns {string[]} feature keys, in registry order
 */
export function deriveSectionFeatures(root, { description = '' } = {}) {
  const tokens = classTokenSet(root);
  const desc = String(description || '').toLowerCase();
  const found = [];
  for (const feature of SECTION_FEATURES) {
    const byClass =
      feature.classes.some((c) => tokens.has(c)) ||
      (feature.classPrefix && [...tokens].some((t) => t.startsWith(feature.classPrefix)));
    const byKeyword = feature.keywords.some((k) => desc.includes(k));
    if (byClass || byKeyword) found.push(feature.key);
  }
  return found;
}

// ── Memoized entry point ──
// Keyed by the html string ITSELF: a content edit produces a new string and
// therefore a new entry; an unchanged body never re-parses, no matter how many
// times `state-changed` fires. Bounded so a long editing session cannot grow
// it without limit — insertion-ordered Map, oldest evicted first.
const FACTS_CACHE_MAX = 500;
const factsCache = new Map();
let parseCount = 0;

/** Test seam: how many real parses have happened. */
export function _factsParseCount() {
  return parseCount;
}

/** Test seam: reset between suites. */
export function _resetFactsCache() {
  factsCache.clear();
  parseCount = 0;
}

/**
 * Facts + features for a section body, memoized by the html string.
 *
 * @param {string} html — the section's stored html
 * @param {{parse: (html: string) => ParentNode, description?: string}} opts —
 *   `parse` is injected by the consumer (e.g. DOMParser in the browser,
 *   jsdom in tests); it is only invoked on a cache miss.
 * @returns {{heading: string, imageCount: number, linkCount: number, features: string[]}}
 */
export function sectionFactsFor(html, { parse, description = '' } = {}) {
  const key = typeof html === 'string' ? html : '';
  if (!key || typeof parse !== 'function') {
    return { heading: '', imageCount: 0, linkCount: 0, features: [] };
  }
  // Description participates in feature derivation but descriptions are
  // per-record stable; key on both so two records sharing a body but not a
  // description cannot serve each other's features.
  const cacheKey = `${description.length}:${description}${key}`;
  const hit = factsCache.get(cacheKey);
  if (hit) return hit;
  parseCount += 1;
  const root = parse(key);
  const facts = {
    ...summarizeSectionDom(root),
    features: deriveSectionFeatures(root, { description })
  };
  factsCache.set(cacheKey, facts);
  if (factsCache.size > FACTS_CACHE_MAX) {
    factsCache.delete(factsCache.keys().next().value);
  }
  return facts;
}
